import 'twin.macro';
import React from 'react';
import Button from '@teetsh/app/src/components/Buttons/Button';
import {
  EyeIcon as Eye,
  PencilIcon as Pencil,
  PlusIcon as Plus,
  TrashIcon as Trash,
} from '@heroicons/react/24/outline';
import SchoolyearButtonLink from '@teetsh/app/src/components/Buttons/SchoolyearButtonLink';
import {
  ILivretStatus,
  LivretEleve,
} from '@teetsh/app/src/containers/Livrets/types/livret';
import livretsUrls from '@teetsh/app/src/containers/Livrets/urls';
import DeleteButton from '@teetsh/app/src/components/Buttons/DeleteButton';
import { useDeleteLivretEleve } from '@teetsh/app/src/containers/Livrets/queries/useDeleteLivretEleve';
import { useGenerateLivretEleves } from '@teetsh/app/src/containers/Livrets/queries/useGenerateLivretEleves';
import { useIsBelowBreakpoint } from '@teetsh/app/src/components/ResponsiveSwitcher/useIsBelowSize';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTranslation } from 'react-i18next';

interface Props {
  livretEleve?: LivretEleve;
  status?: ILivretStatus;
  livretEvalId: string;
  eleveId: string;
  getEleveLink?: (evId: string) => string;
  readonly?: boolean;
}

export function EleveLivretActions(props: Props) {
  const { status, livretEleve, livretEvalId, eleveId, getEleveLink, readonly } =
    props;
  const user = useUser();
  const { t } = useTranslation(['carnet', 'common']);
  const isBelowLg = useIsBelowBreakpoint('xl');
  const { mutateAsync: createLivretEleves, isLoading } =
    useGenerateLivretEleves();
  const deleteLivretEleve = useDeleteLivretEleve({ livretEvalId });
  if (!livretEleve) {
    if (readonly) {
      return null;
    }
    return (
      <div>
        <Button
          onClick={() =>
            createLivretEleves({
              eleveIds: [eleveId],
              livretEvalId,
            })
          }
          large
          loading={isLoading}
          icon={Plus}
        >
          {t('carnet:livret.generate')}
        </Button>
      </div>
    );
  }
  if (status === 'not_started' && !readonly) {
    return (
      <div>
        <SchoolyearButtonLink
          icon={Plus}
          to={`${livretsUrls.livretEleveDetails(
            livretEvalId,
            livretEleve.eleveId,
          )}/generate-livret`}
        >
          {t('carnet:livret.generateLivret')}
        </SchoolyearButtonLink>
      </div>
    );
  }

  return (
    <div tw="flex justify-end">
      {!user.isNotLoggedIn && (
        <DeleteButton
          tw="mr-2"
          onClick={() => deleteLivretEleve(livretEleve.id)}
          withConfirmation
          minimal
          small={isBelowLg}
          icon={Trash}
        />
      )}
      {status === 'draft' && !readonly ? (
        <SchoolyearButtonLink
          to={
            getEleveLink
              ? getEleveLink(livretEleve.eleveId)
              : `${livretsUrls.livretEleveDetails(
                  livretEvalId,
                  livretEleve.eleveId,
                )}`
          }
          withSchoolContext={!getEleveLink}
          icon={Pencil}
          small={isBelowLg}
          minimal={isBelowLg}
          intent="primary"
          aria-label="Modifier le livret"
        >
          {!isBelowLg && <span>{t('common:buttons.modify')}</span>}
        </SchoolyearButtonLink>
      ) : (
        <SchoolyearButtonLink
          to={
            getEleveLink
              ? getEleveLink(livretEleve.eleveId)
              : `${livretsUrls.livretEleveDetails(
                  livretEvalId,
                  livretEleve.eleveId,
                )}`
          }
          withSchoolContext={!getEleveLink}
          icon={Eye}
          small={isBelowLg}
          minimal={isBelowLg}
          aria-label="Visualiser le livret"
        >
          {!isBelowLg && <span>{t('carnet:livret.visualise')}</span>}
        </SchoolyearButtonLink>
      )}
    </div>
  );
}
